
import React, { useContext, useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LabelList,
  PieChart, Pie, Cell, AreaChart, Area
} from 'recharts';
import { 
  Users, Building2, Home, Key, Activity, MapPin, 
  CheckCircle, AlertCircle, Clock, PieChart as PieIcon,
  RefreshCw, LayoutGrid, Layers
} from 'lucide-react';
import { toast } from 'react-toastify';
import useFetch from '../../../../hooks/useFetch';
import UserService from '../../../../services/user.service';
import PropertyService from '../../../../services/property.service';
import DeveloperService from '../../../../services/developer.service';
import AgentService from '../../../../services/agent.service';
import { AuthContext } from '../../../../contexts/AuthContext';
import { PermissionModulesEnum, PermissionActionsEnum, Modules, AuthRoutes } from '../../../../enums';
import { hasResourseAccess } from '../../../../utils/methods';
import { downloadFile } from '../../../../utils/downloadFile';

/**
 * Modern Real Estate Dashboard
 * Designed for Single Screen View with No Scroll
 */

// Custom X-Axis Tick for wrapping text
const CustomAxisTick = (props: any) => {
  const { x, y, payload } = props;
  const words = payload.value ? payload.value.toString().split(' ') : [];
  return (
    <g transform={`translate(${x},${y})`}>
      <text x={0} y={0} dy={10} textAnchor="middle" fill="#6b7280" fontSize={9} style={{fontWeight: 500}}>
        {words.map((word: string, i: number) => (
          <tspan x={0} dy={i === 0 ? 5 : 10} key={i}>
            {word}
          </tspan>
        ))}
      </text>
    </g>
  );
};

const Dashboard: React.FC = () => {
  const { t } = useTranslation();
  
  // Scroll to top on mount to fix persistence issue from other pages
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  // Cast context to any to avoid "unknown" type errors if context isn't strictly typed
  const { permissions, isSystemAdmin, theme } = useContext(AuthContext) as any;
  const [refreshKey, setRefreshKey] = useState(0);
  const [exportingAgents, setExportingAgents] = useState(false);
  const [exportingDevelopers, setExportingDevelopers] = useState(false);

  // Permission Checks
  const canViewUsers = hasResourseAccess(isSystemAdmin, permissions, {
    accessibleTo: [{ [PermissionModulesEnum.Users]: [PermissionActionsEnum.View] }],
  });
  const canViewProperties = hasResourseAccess(isSystemAdmin, permissions, {
    accessibleTo: [{ [PermissionModulesEnum.Properties]: [PermissionActionsEnum.View] }],
  });
  const canViewDevelopers = hasResourseAccess(isSystemAdmin, permissions, {
    accessibleTo: [{ [PermissionModulesEnum.Developers]: [PermissionActionsEnum.View] }],
  });

  // Fetch Data
  const { data: userRaw }: any = useFetch({ service: UserService, func: 'kpis', payload: null, dependency: [refreshKey], skip: !canViewUsers });
  const { data: propertyRaw }: any = useFetch({ service: PropertyService, func: 'kpis', payload: null, dependency: [refreshKey], skip: !canViewProperties });
  const { data: developerRaw }: any = useFetch({ service: DeveloperService, func: 'kpis', payload: null, dependency: [refreshKey], skip: !canViewDevelopers });

  // Normalize Data
  const userData = useMemo(() => userRaw?.records || userRaw || {}, [userRaw]);
  const propertyData = useMemo(() => propertyRaw || {}, [propertyRaw]);
  const developerData = useMemo(() => developerRaw || {}, [developerRaw]);

  // Transform Data for Charts
  
  // 1. Property Categories (Bar)
  const categoryData = useMemo(() => {
    if (!propertyData.properties_by_category) return [];
    return Object.entries(propertyData.properties_by_category).map(([name, value]: any) => ({ name, value }));
  }, [propertyData]);

  // 2. Construction Status (Bar/Funnel)
  const statusData = useMemo(() => {
    if (!propertyData.construction_status_wise_count) return [];
    return Object.entries(propertyData.construction_status_wise_count).map(([name, value]: any) => ({ name, value }));
  }, [propertyData]);

  // 3. BHK Type (Pie)
  const bhkData = useMemo(() => {
    if (!propertyData.properties_by_bhk_type) return [];
    return Object.entries(propertyData.properties_by_bhk_type)
      .map(([name, value]: any) => ({ name, value }))
      .sort((a, b) => b.value - a.value); // Sort for better pie
  }, [propertyData]);

  // 4. Approval Status (Donut)
  const approvalData = useMemo(() => {
    if (!propertyData.properties_by_approval_status) return [];
    return Object.entries(propertyData.properties_by_approval_status).map(([name, value]: any) => ({ name, value }));
  }, [propertyData]);

  // 5. User Roles (Bar)
  const userRolesData = useMemo(() => {
    const roles = ['admin_users', 'back_office_users', 'channel_partner_users', 'developer_users'];
    return roles.map(role => ({
      name: role.replace('_users', '').replace('_', ' ').toUpperCase(),
      value: userData[role] || 0
    }));
  }, [userData]);

  // 6. City Wise (Simple List or Bar)
  const cityData = useMemo(() => {
    if (!propertyData.properties_by_city) return [];
    return Object.entries(propertyData.properties_by_city).map(([name, value]: any) => ({ name, value }));
  }, [propertyData]);
  
  // 7. Property Type (Flat, Villa etc)
  const typeData = useMemo(() => {
    if (!propertyData.properties_by_property_type) return [];
    return Object.entries(propertyData.properties_by_property_type).map(([name, value]: any) => ({ name, value }));
  }, [propertyData]);


  // Colors
  // Colors - Updated to match project palette 
  const COLORS = ['#ed6261', '#26c6f9', '#fdb528', '#72e128', '#6f42c1', '#e83e8c', '#20c997'];
  const STATUS_COLORS = {
    'Approved': '#10b981',
    'Pending': '#f59e0b',
    'Rejected': '#ef4444'
  };

  const handleRefresh = () => {
    setRefreshKey(prev => prev + 1);
  };

  const handleExportAgents = async () => {
    setExportingAgents(true);
    try {
      const blob: any = await AgentService.exportAll();
      downloadFile(blob, 'agents.xlsx');
      toast.success('Agents exported successfully');
    } catch {
      // HTTP interceptor already shows the error toast
    } finally {
      setExportingAgents(false);
    }
  };

  const handleExportDevelopers = async () => {
    setExportingDevelopers(true);
    try {
      const blob: any = await DeveloperService.exportAll();
      downloadFile(blob, 'developers.xlsx');
      toast.success('Developers exported successfully');
    } catch {
      // HTTP interceptor already shows the error toast
    } finally {
      setExportingDevelopers(false);
    }
  };

  const isLight = theme !== 'dark';
  
  // Helper for "Old Style" Card
  const StatColumnCard = ({ title, total, active, inactive, iconKey, color, link, activeLabel, inactiveLabel, totalForPercent }: any) => {
     // Default logic
     const percentBase = totalForPercent || total;
     const activePercent = percentBase > 0 ? Math.round((active / percentBase) * 100) : 0;
     
     const actLabel = activeLabel || "Active";
     const inactLabel = inactiveLabel || "Inactive";
     
     // Icon mapping
     const Icons: any = { 
         'users': 'ri-group-line', 
         'developer': 'ri-building-2-line', 
         'property': 'ri-building-line', 
         'key': 'ri-key-2-line' 
     };
      const iconClass = Icons[iconKey] || 'ri-layout-grid-line';

      return (
        <div className="card h-100 border-0 shadow-sm overflow-hidden position-relative" 
             style={{ borderRadius: '16px', backgroundColor: isLight ? '#fff' : '#1f2937' }}>
            
            {/* Top Right Decorative Circle (Restored & Enhanced) */}
            <div className={`bg-${color}`} 
                 style={{
                     position: 'absolute', 
                     top: '-20px', 
                     right: '-20px', 
                     width: '100px', 
                     height: '100px', 
                     borderRadius: '50%', 
                     opacity: 0.15,
                     pointerEvents: 'none'
                 }}></div>

            <div className="card-body p-3 position-relative z-1 d-flex flex-column h-100 justify-content-between">
                
                {/* Header: Title and Top Right Icon */}
                <div className="d-flex justify-content-between align-items-start mb-0">
                    <h6 className="text-uppercase small fw-bold mb-0 mt-1" style={{letterSpacing: '0.5px'}}>{title}</h6>
                    
                    {/* Icon floating in the corner circle area */}
                    <div className={`text-${color} p-1 rounded-circle bg-${color} bg-opacity-10 d-flex align-items-center justify-content-center`} 
                         style={{width: '42px', height: '42px'}}>
                        <i className={`${iconClass} ri-xl`}></i>
                    </div>
                </div>

                {/* Big Metric */}
                <div className="mb-0">
                    <h3 className={`fw-bold mb-0 text-${color}`} style={{fontSize: '1.8rem'}}>{total}</h3>
                </div>

                {/* Footer: Stats & Link */}
                <div className="d-flex align-items-center justify-content-between mt-auto">
                     {/* Compact Stats */}
                     <div className="d-flex align-items-center gap-3 small">
                        <span className="d-flex align-items-center fw-medium text-success" title="Active">
                            <i className="ri-checkbox-circle-fill me-1"></i>{active} <span className="ms-1 text-muted" style={{opacity: 0.8}}>{actLabel}</span>
                        </span>
                        <span className="d-flex align-items-center fw-medium text-muted" title="Inactive">
                            <i className="ri-close-circle-fill me-1"></i>{inactive} <span className="ms-1 text-muted" style={{opacity: 0.8}}>{inactLabel}</span>
                        </span>
                    </div>

                    {link && (
                        <Link to={link || '#'} className="btn btn-icon btn-ghost-secondary rounded-circle btn-sm stretched-link" style={{width: '24px', height:'24px', padding:0, minHeight: 0}}>
                                <i className="ri-arrow-right-line"></i>
                        </Link>
                    )}
                </div>
            </div>
        </div>
      );
  }

  // Inline styles to guarantee layout
  const styles = {
    container: {
      height: 'calc(100vh - 70px)', // Adjust for potential global header
      width: '100%',
      overflowY: 'auto' as 'auto',
      padding: '1rem',
      display: 'flex',
      flexDirection: 'column' as const,
      gap: '1rem',
      backgroundColor: isLight ? '#f8f9fa' : '#14141f'
    },
    gridRow: {
        display: 'grid',
        gap: '1rem',
        width: '100%'
    },

    // Standard Card Style for Charts
    card: {
        borderRadius: '16px',
        padding: '1rem',
        boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
        backgroundColor: isLight ? '#fff' : '#1f2937',
        border: `1px solid ${isLight ? '#e5e7eb' : '#374151'}`,
        display: 'flex',
        flexDirection: 'column' as const,
        justifyContent: 'center',
        position: 'relative' as const,
        overflow: 'hidden'
    },
    decorativeCircle: {
        position: 'absolute' as const,
        top: 0,
        right: 0,
        marginRight: '-30px',
        marginTop: '-30px',
        width: '120px',
        height: '120px',
        borderRadius: '50%',
        zIndex: 0,
        pointerEvents: 'none' as const
    },
    metricValue: {
        fontSize: '1.75rem',
        fontWeight: '700',
        marginBottom: '0.25rem',
        lineHeight: 1.2
    },
    metricLabel: {
        fontSize: '0.75rem',
        textTransform: 'uppercase' as const,
        letterSpacing: '0.5px',
        color: isLight ? '#6b7280' : '#9ca3af',
        fontWeight: '600'
    },
    chartTitle: {
        fontSize: '0.9rem',
        fontWeight: '600',
        marginBottom: '0.5rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        color: isLight ? '#374151' : '#e5e7eb'
    }
  };

  return (
    <div style={styles.container}>
      {/* Export Actions — visible to system admin only */}
      {isSystemAdmin === 'true' && (
        <div className="d-flex justify-content-end align-items-center gap-2">
          <button
            className="btn btn-sm btn-outline-primary d-flex align-items-center gap-1"
            onClick={handleExportAgents}
            disabled={exportingAgents}
          >
            {exportingAgents ? (
              <>
                <span
                  className="spinner-border spinner-border-sm"
                  role="status"
                  aria-hidden="true"
                />
                Exporting...
              </>
            ) : (
              <>
                <i className="ri-download-2-line" />
                Export Agents
              </>
            )}
          </button>

          <button
            className="btn btn-sm btn-outline-primary d-flex align-items-center gap-1"
            onClick={handleExportDevelopers}
            disabled={exportingDevelopers}
          >
            {exportingDevelopers ? (
              <>
                <span
                  className="spinner-border spinner-border-sm"
                  role="status"
                  aria-hidden="true"
                />
                Exporting...
              </>
            ) : (
              <>
                <i className="ri-download-2-line" />
                Export Developers
              </>
            )}
          </button>
        </div>
      )}

      {/* HEADER & TOP STATS */}
      <div style={{...styles.gridRow, gridTemplateColumns: 'repeat(4, 1fr)', height: '18%', minHeight: '130px'}}>
         {/* User Stat (Old Style) */}
         <div style={{minHeight: 0}}>
             <StatColumnCard 
                 title={t('Total Users')}
                 total={userData.total_users || 0}
                 active={userData.active_users || 0}
                 inactive={userData.inactive_users || 0}
                 iconKey="users"
                 color="info"
                 link={`${Modules.users}${AuthRoutes.users}`}
             />
         </div>

         {/* Developer Stat (Old Style) */}
         <div style={{minHeight: 0}}>
             <StatColumnCard 
                 title={t('Total Developers')}
                 total={developerData.total_developers || 0}
                 active={developerData.active_developers || 0}
                 inactive={developerData.inactive_developers || 0}
                 iconKey="developer"
                 color="warning"
                 link={`${Modules.developers}${AuthRoutes.developers}`}
             />
         </div>

         {/* Property Stat (Old Style) */}
         <div style={{minHeight: 0}}>
            <StatColumnCard 
                 title={t('Total Properties')}
                 total={propertyData.total_properties || 0}
                 active={propertyData.active_properties || 0}
                 inactive={propertyData.inactive_properties || 0}
                 iconKey="property"
                 color="danger"
                 link={`${Modules.properties}${AuthRoutes.properties}`}
             />
         </div>

         {/* Possession Ready (Highlight) - kept styled as highlight for impact, but matches height */}
         <div style={{minHeight: 0}}>
            <StatColumnCard 
                 title={t('Possession Ready')}
                 total={propertyData.current_month_possession_count || 0}
                 active={propertyData.current_month_possession_count || 0}
                 inactive={propertyData.construction_status_wise_count?.['Ready to Move'] || 0}
                 totalForPercent={propertyData.total_properties}
                 iconKey="key"
                 color="success"
                 link={`${Modules.properties}${AuthRoutes.properties}`}
                 activeLabel="This Month"
                 inactiveLabel="Total Ready"
             />
         </div>
      </div>

      {/* MID ROW: Charts */}
      <div style={{...styles.gridRow, gridTemplateColumns: '1fr 1fr 1fr', height: '38%', minHeight: 0}}>
         
         {/* Construction Status */}
         <div style={styles.card}>
            <div style={styles.chartTitle}>
                <span>{t('dashboard.charts.constructionStatus')}</span>
                <i className="ri-layout-grid-line text-muted ri-lg"></i>
           </div>
            {/* Decor */}
            <div style={{...styles.decorativeCircle, background: '#8b5cf6', opacity: 0.15}}></div>
            <div style={{flex: 1, minHeight: 0, width: '100%', position: 'relative', zIndex: 1}}>
               <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={statusData} layout="vertical" margin={{top: 5, right: 30, left: 40, bottom: 5}}>
                     <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e5e7eb" />
                     <XAxis type="number" hide />
                     <YAxis dataKey="name" type="category" width={100} tick={{fontSize: 10}} interval={0} />
                     <Tooltip cursor={{fill: 'transparent'}} content={<CustomTooltip />} />
                     <Bar dataKey="value" fill="#8b5cf6" radius={[0, 4, 4, 0]} barSize={15}>
                        <LabelList dataKey="value" position="right" style={{ fill: '#6b7280', fontSize: '10px' }} />
                     </Bar>
                  </BarChart>
               </ResponsiveContainer>
            </div>
         </div>

         {/* Property Categories */}
         <div style={styles.card}>
            <div style={styles.chartTitle}>
                <span>{t('dashboard.charts.propertyCategories')}</span>
                <i className="ri-pie-chart-2-line text-muted ri-lg"></i>
           </div>
            {/* Decor */}
            <div style={{...styles.decorativeCircle, background: '#ec4899', opacity: 0.15}}></div>
            <div className="d-flex align-items-center h-100 position-relative z-1" style={{minHeight: 0}}>
                 <div style={{flex: 1, height: '100%'}}>
                     <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                           <Pie
                             data={categoryData}
                             cx="50%"
                             cy="50%"
                             innerRadius={0}
                             outerRadius={100}
                             paddingAngle={0}
                             dataKey="value"
                           >
                             {categoryData.map((entry: any, index: number) => (
                               <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                             ))}
                           </Pie>
                           <Tooltip content={<CustomTooltip />} />
                        </PieChart>
                     </ResponsiveContainer>
                 </div>
                 <div className="d-flex flex-column justify-content-center pe-2" style={{fontSize: '13px', minWidth: '120px', maxHeight: '100%', overflowY: 'auto'}}>
                     {categoryData.map((item, idx) => (
                          <div key={idx} className="mb-1 d-flex align-items-center">
                              <span className="rounded-circle me-1 flex-shrink-0" style={{width: 10, height: 10, background: COLORS[idx % COLORS.length]}}></span>
                              <span className="muted text-truncate" style={{maxWidth: '95px'}} title={item.name}>{item.name}</span>
                              <b className="ms-1">{item.value}</b>
                          </div>
                     ))}
                 </div>
            </div>
         </div>

         {/* User Roles & Approvals */}
          {/* Approval Status (Now Full Height) */}
          <div style={styles.card}>
             <div style={styles.chartTitle}>
                 <span>{t('dashboard.charts.approvalStatus')}</span>
                 <i className="ri-checkbox-circle-line text-muted ri-lg"></i>
            </div>
             {/* Decor */}
             <div style={{...styles.decorativeCircle, background: '#10b981', opacity: 0.15}}></div>
             <div className="d-flex align-items-center h-100 position-relative z-1">
                 <div style={{flex: 1, height: '100%'}}>
                     <ResponsiveContainer width="100%" height="100%">
                         <PieChart>
                             <Pie data={approvalData} cx="50%" cy="50%" innerRadius={60} outerRadius={80} dataKey="value">
                                 {approvalData.map((entry: any, index) => (
                                     <Cell key={`cell-${index}`} fill={STATUS_COLORS[entry.name as keyof typeof STATUS_COLORS] || COLORS[index]} />
                                 ))}
                             </Pie>
                             <Tooltip content={<CustomTooltip />} />
                         </PieChart>
                     </ResponsiveContainer>
                 </div>
                 <div className="d-flex flex-column justify-content-center pe-3" style={{fontSize: '14px', minWidth: '120px'}}>
                     {approvalData.map((item, idx) => (
                          <div key={idx} className="mb-2 d-flex align-items-center">
                              <span className="rounded-circle me-2" style={{width: 12, height: 12, background: STATUS_COLORS[item.name as keyof typeof STATUS_COLORS] || COLORS[idx]}}></span>
                              <span className="muted">{item.name}: <b>{item.value}</b></span>
                          </div>
                     ))}
                 </div>
             </div>
          </div>

      </div>

      {/* BOTTOM ROW: Detailed Breakdowns */}
      <div style={{...styles.gridRow, gridTemplateColumns: '2.5fr 1fr 0.8fr', height: '38%', minHeight: 0, paddingBottom: '0.5rem'}}>
          
          {/* Property Types & BHK Combined View */}
          <div style={styles.card}>
             <div style={styles.chartTitle}>
                 <span>{t('dashboard.charts.inventoryDistribution')}</span>
                 <i className="ri-stack-line text-muted ri-lg"></i>
            </div>
             {/* Decor */}
             <div style={{...styles.decorativeCircle, background: '#ec4899', opacity: 0.15}}></div>
             <div className="row h-100 g-0 position-relative z-1">
                 <div className="col-7 h-100 border-end">
                     <ResponsiveContainer width="95%" height="100%">
                        <BarChart data={typeData} margin={{top: 20, right: 10, left: -20, bottom: 0}}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f3f4f6" />
                            <XAxis dataKey="name" tick={<CustomAxisTick />} interval={0} />
                            <YAxis tick={{fontSize: 10}} />
                            <Tooltip content={<CustomTooltip />} />
                            <Bar dataKey="value" fill="#ec4899" radius={[4, 4, 0, 0]} barSize={30}>
                                <LabelList dataKey="value" position="top" style={{ fill: '#6b7280', fontSize: '10px' }} />
                            </Bar>
                        </BarChart>
                     </ResponsiveContainer>
                 </div>
                 <div className="col-5 h-100 ps-2">
                     <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={bhkData} layout="vertical" margin={{top: 0, right: 25, left: 40, bottom: 0}}>
                            <XAxis type="number" hide />
                            <YAxis dataKey="name" type="category" width={40} tick={{fontSize: 10}} interval={0} />
                            <Tooltip content={<CustomTooltip />} />
                            <Bar dataKey="value" fill="#3b82f6" radius={[0, 4, 4, 0]} barSize={10}>
                                <LabelList dataKey="value" position="right" style={{ fill: '#6b7280', fontSize: '10px' }} />
                            </Bar>
                        </BarChart>
                     </ResponsiveContainer>
                 </div>
             </div>
          </div>

          {/* City Stats */}
          <div style={styles.card}>
              <div style={styles.chartTitle}>
                  <span>{t('dashboard.charts.cityDistribution')}</span>
                  <i className="ri-map-pin-line text-muted ri-lg"></i>
              </div>
              {/* Decor */}
              <div style={{...styles.decorativeCircle, background: '#8b5cf6', opacity: 0.15}}></div>
              <div className="d-flex align-items-center h-100 position-relative z-1">
                 {cityData.length > 0 ? (
                    <>
                        <div style={{flex: 1, height: '100%'}}>
                             <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie data={cityData} cx="50%" cy="50%" innerRadius={40} outerRadius={60} dataKey="value">
                                        {cityData.map((entry: any, index: number) => (
                                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <Tooltip content={<CustomTooltip />} />
                                </PieChart>
                             </ResponsiveContainer>
                        </div>
                        <div className="d-flex flex-column justify-content-center pe-3" style={{fontSize: '12px', minWidth: '80px'}}>
                            {cityData.map((item, idx) => (
                                 <div key={idx} className="mb-2 d-flex align-items-center">
                                     <span className="rounded-circle me-2" style={{width: 10, height: 10, background: COLORS[idx % COLORS.length]}}></span>
                                     <span className="muted">{item.name}: <b>{item.value}</b></span>
                                 </div>
                            ))}
                        </div>
                    </>
                 ) : (
                     <div className="text-center text-muted text-sm w-100">{t('dashboard.charts.noCityData')}</div>
                 )}
              </div>
          </div>

          {/* Overall Health / Quick Summary Table */}
          {/* User Roles (Moved from Mid Row) */}
          <div style={styles.card}>
              <div style={styles.chartTitle}>
                 <span>{t('dashboard.charts.userRoles')}</span>
                 <i className="ri-group-line text-muted ri-lg"></i>
            </div>
             {/* Decor */}
             <div style={{...styles.decorativeCircle, background: '#6366f1', opacity: 0.15}}></div>
             <ResponsiveContainer width="100%" height="100%" className="position-relative z-1">
                 <BarChart data={userRolesData} margin={{top: 20, right: 0, left: 0, bottom: 0}}>
                     <Tooltip content={<CustomTooltip />} />
                     <Bar dataKey="value" fill="#6366f1" radius={[4, 4, 0, 0]} barSize={20}>
                        <LabelList dataKey="value" position="top" style={{ fill: '#6b7280', fontSize: '10px' }} />
                     </Bar>
                     <XAxis dataKey="name" tick={<CustomAxisTick />} interval={0} />
                 </BarChart>
             </ResponsiveContainer>
          </div>

      </div>

      {/* Global CSS for Animations & Scroll Hide */}
      <style>{`
        body { overflow: hidden; /* Force single screen */ }
        ::-webkit-scrollbar { display: none; }
        .recharts-bar-rectangle { transition: all 0.3s; }
        .recharts-bar-rectangle:hover { opacity: 0.8; }
        .hover-lift:hover { transform: translateY(-5px); box-shadow: 0 10px 20px rgba(0,0,0,0.1); }
      `}</style>
    </div>
  );
};

// Helper for Custom Tooltip
const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-2 border shadow-sm rounded text-dark" style={{fontSize: '12px', zIndex: 1000}}>
        <p className="mb-1 fw-bold">{label || payload[0].payload.name}</p>
        <p className="mb-0 text-primary">
          {payload[0].name === 'value' ? 'Count' : payload[0].name}: {payload[0].value}
        </p>
      </div>
    );
  }
  return null;
};

export default Dashboard;
